import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService, GlobalsService, SleepService, UtilsService, NotificationsService, JobsService, QuickbooksService, TasksService, AppointmentsService } from "@services";
import { SlickToastService } from "slick-components";
import { JobEditDialogComponent } from "@app/jobs";
import { QuickbooksTaskDisplayDialogComponent } from "@app/quickbooks";
import { TaskEditDialogComponent } from "@app/tasks/tasks-components";
import { AppointmentDialogComponent } from "@app/appointments";
import { AppointmentTypes, INotificationModel, INotificationItemModel, IJobModel } from "@models";
import { MessageQueueDispatcher } from "@message-queue";
import { RemindersStore, ScheduleAppointmentStore, TasksStore } from "@stores";
import { Subscription } from "rxjs";
import moment from "moment";

/**
 * The one place an in-app notification is shown.
 *
 * Three things used to notify independently and look nothing alike: server notifications (a sticky
 * toast plus the bell list), task reminders (a sticky toast plus a separate dialog hanging off the
 * Tasks menu) and appointment reminders (a sticky toast and nothing else - they never reached the
 * bell at all).  Every one of them is a server row now - the two reminders are raised by
 * RemindersStore when their time comes - and they all arrive here the same way, are normalised onto
 * INotificationItemModel, and render as the same card with the same actions: dismiss, snooze, and a
 * view action for whatever it is about.
 *
 * The flag opens the same panel an arriving notification opens, so there is only ever one list.
 */
@Component({
    selector: "notifications",
    templateUrl: "notifications.component.html",
    styleUrls: ['notifications.component.css'],
    providers: [
        SlickToastService, JobsService, TasksService, AppointmentsService,
        // The hosted appointment dialog injects this schedule store (normally provided by the schedule
        // modules); provide our own instance so the dialog works from the header without a schedule view
        // present.  Same reason global-search-bar, the other dialog host in this module, provides it.
        ScheduleAppointmentStore
    ],
    standalone: false
})

export class NotificationsComponent implements OnInit, OnDestroy {
	@ViewChild("jobEditDialogRef", { static: true }) jobEditDialogRef: JobEditDialogComponent;
	@ViewChild("quickbooksTaskDisplayDialog", { static: true }) quickbooksTaskDisplayDialog: QuickbooksTaskDisplayDialogComponent;
	@ViewChild("taskEditDialogRef", { static: true }) taskEditDialogRef: TaskEditDialogComponent;
	@ViewChild("appointmentDialogRef", { static: true }) appointmentDialogRef: AppointmentDialogComponent;
	@ViewChild("notificationPanelRef", { static: true }) notificationPanelRef: ElementRef<HTMLElement>;
	@ViewChild("notificationAlertsRef", { static: true }) notificationAlertsRef: ElementRef<HTMLElement>;
	@ViewChild("flagRef", { static: true }) flagRef: ElementRef<HTMLElement>;

	/** The label, icon and tint for each notification type the server can raise. */
	private static readonly notificationTypes: { [notificationType: string]: { typeLabel: string, icon: string, tone?: "warning" | "danger" } } = {
		JobAssigned: { typeLabel: "Job Assigned", icon: "fa-clipboard-list" },
		Task: { typeLabel: "Task Assigned", icon: "fa-list-check" },
		AppointmentAssigned: { typeLabel: "Appointment Assigned", icon: "fa-calendar-day" },
		AppointmentReminder: { typeLabel: "Appointment Reminder", icon: "fa-clock" },
		TaskReminder: { typeLabel: "Task Reminder", icon: "fa-clock" },
		PayableAssigned: { typeLabel: "Payable Assigned", icon: "fa-file-invoice-dollar" },
		NewPaymentReceived: { typeLabel: "Payment Received", icon: "fa-money-check-dollar" },
		PaymentFailed: { typeLabel: "Payment Failed", icon: "fa-money-check-dollar", tone: "danger" },
		TaskCompleted: { typeLabel: "Task Completed", icon: "fa-list-check" },
		WorkOrderAccepted: { typeLabel: "Work Order Accepted", icon: "fa-clipboard-check" },
		ServiceAgreementSigned: { typeLabel: "Agreement Signed", icon: "fa-file-signature" },
		ChangeOrderSigned: { typeLabel: "Change Order Signed", icon: "fa-file-signature" },
		CertificateOfCompletionSigned: { typeLabel: "Certificate of Completion Signed", icon: "fa-file-signature" },
		AllServiceAgreementsSigned: { typeLabel: "All Agreements Signed", icon: "fa-file-circle-check" },
		ServiceAgreementDeclined: { typeLabel: "Agreement Declined", icon: "fa-file-xmark", tone: "warning" },
		QuickbooksError: { typeLabel: "QuickBooks Error", icon: "fa-triangle-exclamation", tone: "danger" },
		InventoryReorderThreshold: { typeLabel: "Low Stock", icon: "fa-shelves", tone: "warning" },
		ClientPortalUpload: { typeLabel: "Client Portal Upload", icon: "fa-cloud-arrow-up" },
		EmailSendFailed: { typeLabel: "Email Failed", icon: "fa-envelope-dot", tone: "danger" },
		TextChatSendFailed: { typeLabel: "Text Failed", icon: "fa-comment-exclamation", tone: "danger" },
		JobChecklistCompleted: { typeLabel: "Checklist Completed", icon: "fa-clipboard-list-check" },
		MitigationCompleted: { typeLabel: "Mitigation Completed", icon: "fa-droplet" },
		NewXactAnalysis: { typeLabel: "New Assignment", icon: "fa-inbox-in" }
	};

	// setTimeout is a signed 32-bit millisecond count.
	private static readonly MAX_TIMEOUT_MS = 2147483647;

	// Must stay in step with the .notification-flyer transition in the stylesheet.
	private static readonly FLY_TO_FLAG_MS = 450;

	// Must stay in step with the notification-flag-pulse keyframes.
	private static readonly FLAG_PULSE_MS = 450;

	/** The same short list the task reminder has always offered, plus an hour for the longer ones. */
	readonly snoozeOptions = [
		{ snoozeMinutes: 1, text: "1 min" },
		{ snoozeMinutes: 5, text: "5 min" },
		{ snoozeMinutes: 10, text: "10 min" },
		{ snoozeMinutes: 15, text: "15 min" },
		{ snoozeMinutes: 30, text: "30 min" },
		{ snoozeMinutes: 60, text: "1 hour" }
	];

	/** Everything live, snoozed or not.  The two lists below are what the panel actually renders. */
	private notifications: INotificationItemModel[] = [];

	activeNotifications: INotificationItemModel[] = [];
	snoozedNotifications: INotificationItemModel[] = [];

	/**
	 * The alert stack under the flag - what has arrived and not been dealt with yet.  This is the only
	 * thing that appears on its own; the panel is never opened for the user, only by the user.
	 */
	alertNotifications: INotificationItemModel[] = [];
	private alertKeys = new Set<string>();

	/** Briefly true while a snoozed card is flying into the flag, to make the flag answer back. */
	flagPulse = false;

	/** The stack's own Snooze All duration row. */
	showAlertSnoozeOptions = false;

	showingNotifications = false;
	jobModel: IJobModel;

	addNotification$: Subscription;
	reminderFired$: Subscription;
	connected$: Subscription;

	// Nothing is re-read until the first load has landed - there would be nothing to compare against.
	private initialLoadComplete = false;

	private snoozeTimer: any;

	// Only attached when the panel was opened from the flag.  A panel that opened itself because
	// something arrived stays up until it's dealt with - a stray click elsewhere isn't "I've read it".
	private fnDocumentClick = (e) => this.documentClick(e);
	private documentClickAttached = false;

	constructor(private authService: AuthService,
		private notificationsService: NotificationsService,
		private messageQueueDispatcher: MessageQueueDispatcher,
		private remindersStore: RemindersStore,
		private tasksStore: TasksStore,
		private slickToastService: SlickToastService,
		private jobsService: JobsService,
		private tasksService: TasksService,
		private appointmentsService: AppointmentsService,
		private router: Router,
		private quickbooksService: QuickbooksService) { }

	async ngOnInit() {
		this.movePanelToBody();

		const notificationModels = await this.notificationsService.getActiveNotifications();

		this.notifications = (notificationModels || []).map(x => this.toNotificationItem(x));
		this.refresh();

		this.initialLoadComplete = true;

		if (this.authService.isAuthenticated === true) {
			this.addNotification$ = this.messageQueueDispatcher.addNotification
				.subscribe((notificationModel: INotificationModel) => this.addNotification(notificationModel));

			// A push is only delivered to a connection that was up at the time, so the list is re-read
			// whenever the hub comes back rather than waiting for the next page load - and the reminder
			// timer re-asked, since a nudge to re-arm it may have been missed the same way.
			this.connected$ = this.messageQueueDispatcher.connected
				.subscribe(() => {
					this.reloadNotifications();
					this.remindersStore.reload();
				});

			// The two reminders - appointment and task - are rows raised when their time comes, off a
			// single timer the store keeps.  They arrive here exactly as a pushed notification does.
			this.reminderFired$ = this.remindersStore.fired$
				.subscribe((notificationModel: INotificationModel) => this.addNotification(notificationModel));
		}
	}

	/**
	 * The panel is rendered into <body> rather than left where it is declared.
	 *
	 * It lives inside .menu-right, which the header gives `position: relative; z-index: 1` so the page's
	 * own header bar slides under the controls.  That makes it a stacking context, so the panel's z-index
	 * only ever competes with its siblings inside it - against the page the whole cluster is z-index 1,
	 * and any page element above that paints straight over the panel.  The dashboard's Jobs Closed
	 * Comparison tile does exactly that with `.chart-vertical-slider-wrapper { z-index: 9 }`, which is
	 * what showed the chart through the notifications.
	 *
	 * Angular keeps updating it normally - moving a node doesn't detach it from the view - and the
	 * emulated-encapsulation attributes travel with it, so the component's own styles still apply.
	 * SlickToastService does the same thing with its #slick-toasts container.
	 */
	private movePanelToBody() {
		if (this.notificationPanelRef?.nativeElement)
			document.body.appendChild(this.notificationPanelRef.nativeElement);

		if (this.notificationAlertsRef?.nativeElement)
			document.body.appendChild(this.notificationAlertsRef.nativeElement);
	}

	ngOnDestroy() {
		clearTimeout(this.snoozeTimer);
		this.detachDocumentClick();

		// Angular removes its own view nodes, but these two are no longer where it left them.
		this.notificationPanelRef?.nativeElement?.remove();
		this.notificationAlertsRef?.nativeElement?.remove();

		if (this.addNotification$)
			this.addNotification$.unsubscribe();

		if (this.reminderFired$)
			this.reminderFired$.unsubscribe();

		if (this.connected$)
			this.connected$.unsubscribe();
	}

	/**
	 * Re-reads the server's notifications after the hub connects.  SignalR replays nothing, so anything
	 * raised while the connection was down would otherwise sit unseen until the next page load.  The two
	 * browser-timed reminders have no row behind them, so they are left exactly as they are.
	 */
	private async reloadNotifications() {
		if (this.initialLoadComplete === false)
			return;

		const notificationModels = await this.notificationsService.getActiveNotifications();

		if (!notificationModels)
			return;

		const knownKeys = new Set(this.notifications.filter(x => x.source === "notification").map(x => x.key));
		const items = notificationModels.map(x => this.toNotificationItem(x));

		// The server is the authority on its own rows - cleared and snoozed included - so they are
		// replaced outright rather than merged.
		this.notifications = [...this.notifications.filter(x => x.source !== "notification"), ...items];

		// Whatever arrived while we were away goes on the alert stack, exactly as a live push would have.
		items.filter(x => knownKeys.has(x.key) === false && this.isSnoozed(x) === false)
			.forEach(x => this.alertKeys.add(x.key));

		this.refresh();
	}

	//*****************************************************************************
	//* Building the cards
	//*****************************************************************************

	/**
	 * Notification messages are built server-side and stored as one string, but several of them are
	 * written as lines - a lead-in, then the subject, then the detail.  The card styles them by
	 * position, which is why the line order in NotificationServices is a contract.
	 */
	private messageLines(message: string): string[] {
		return (message || "").replace(/\r\n/g, "\n").split("\n").filter(x => x.trim().length > 0);
	}

	private toNotificationItem(notificationModel: INotificationModel): INotificationItemModel {
		const notificationType = NotificationsComponent.notificationTypes[notificationModel.notificationType]
			|| { typeLabel: "Notification", icon: "fa-bell" };

		const item: INotificationItemModel = {
			key: "notification|" + notificationModel.notificationId,
			source: "notification",
			notificationType: notificationModel.notificationType,
			icon: notificationType.icon,
			typeLabel: notificationType.typeLabel,
			tone: notificationType.tone || "info",
			lines: this.messageLines(notificationModel.message),
			notificationDate: notificationModel.notificationDate,
			snoozeUntil: notificationModel.snoozeUntil,
			notificationId: notificationModel.notificationId,
			jobId: notificationModel.jobId
		};

		// ReferenceId is whatever the notification points at, so what the view action opens depends on
		// the type.  Anything not listed here still gets View Job when the notification carries a job.
		switch (notificationModel.notificationType) {
			case "Task":
			case "TaskReminder":
			case "TaskCompleted":
				item.taskId = notificationModel.referenceId;
				break;

			case "AppointmentAssigned":
			case "AppointmentReminder":
				item.appointmentId = notificationModel.referenceId;
				break;

			case "QuickbooksError":
				item.quickbooksTaskId = notificationModel.referenceId;
				break;

			case "PayableAssigned":
				item.route = "/payables";
				item.routeLabel = "View Payable";
				break;

			case "NewPaymentReceived":
			case "PaymentFailed":
				item.route = "/payments";
				item.routeLabel = "View Payment";
				break;

			case "InventoryReorderThreshold":
				item.route = "/inventory";
				item.routeLabel = "View Inventory";
				break;

			case "EmailSendFailed":
				item.route = "/emailLogs";
				item.routeLabel = "View Email Log";
				break;

			case "TextChatSendFailed":
				item.route = "/text-chat";
				item.routeLabel = "Open Text Chat";
				break;
		}

		return item;
	}

	/** Same shape as the server messages: lead-in, then the task, then when it's due and the detail. */
	addNotification(notificationModel: INotificationModel) {
		this.addItem(this.toNotificationItem(notificationModel));
	}

	/**
	 * One card per row.  A re-read or a reminder raised in two tabs can hand over a row the panel already
	 * has, so a newer copy replaces the older rather than stacking.
	 */
	private addItem(item: INotificationItemModel) {
		this.notifications = this.notifications.filter(x => this.isSameSubject(x, item) === false);
		this.notifications.unshift(item);

		// Arriving puts it on the alert stack under the flag.  The panel stays shut - it is the user's
		// list, opened by the flag, and having it fling itself open over the page was the complaint.
		this.alertKeys.add(item.key);

		this.refresh();
	}

	private isSameSubject(a: INotificationItemModel, b: INotificationItemModel): boolean {
		if (a.source !== b.source)
			return false;

		switch (b.source) {
			case "notification":
				return a.notificationId === b.notificationId;
		}

		return false;
	}

	isSnoozed(item: INotificationItemModel): boolean {
		return !!item.snoozeUntil && moment(item.snoozeUntil).isAfter(moment());
	}

	trackByKey(index: number, item: INotificationItemModel): string {
		return item.key;
	}

	/** Re-splits the list into what's showing and what's snoozed, and re-arms the un-snooze timer. */
	private refresh() {
		this.notifications.sort((a, b) => moment(b.notificationDate).valueOf() - moment(a.notificationDate).valueOf());

		this.activeNotifications = this.notifications.filter(x => this.isSnoozed(x) === false);
		this.snoozedNotifications = this.notifications.filter(x => this.isSnoozed(x));

		// A snoozed card drops off the stack and comes back on it when the snooze is up.
		this.alertNotifications = this.activeNotifications.filter(x => this.alertKeys.has(x.key));

		// The bulk actions only appear once several have piled up, so the row goes with them.
		if (this.alertNotifications.length <= 1)
			this.showAlertSnoozeOptions = false;

		this.armSnoozeTimer();
	}

	/**
	 * A snooze that never came back would just be a slower dismiss, so the panel puts the notification
	 * up again itself when the snooze runs out.
	 */
	private armSnoozeTimer() {
		clearTimeout(this.snoozeTimer);

		const nextSnoozeEnd = this.snoozedNotifications
			.map(x => moment(x.snoozeUntil).diff(moment(), "milliseconds"))
			.sort((a, b) => a - b)[0];

		if (nextSnoozeEnd === undefined)
			return;

		// A little past the end, so isSnoozed() has definitely gone false by the time this runs.
		const delay = Math.min(Math.max(nextSnoozeEnd + 250, 250), NotificationsComponent.MAX_TIMEOUT_MS);

		// Only the ones snoozed right now, so a card the user already dealt with isn't dragged back.
		const snoozedKeys = this.snoozedNotifications.map(x => x.key);

		this.snoozeTimer = setTimeout(() => {
			// Back onto the alert stack - a snooze running out is the same event as it arriving.
			this.notifications
				.filter(x => snoozedKeys.indexOf(x.key) >= 0 && this.isSnoozed(x) === false)
				.forEach(x => this.alertKeys.add(x.key));

			this.refresh();
		}, delay);
	}

	private removeItem(item: INotificationItemModel) {
		this.notifications = this.notifications.filter(x => x.key !== item.key);
		this.alertKeys.delete(item.key);

		this.refresh();

		if (this.notifications.length === 0)
			this.hideNotificationWindow();
	}

	//*****************************************************************************
	//* The panel
	//*****************************************************************************

	showNotifications(event?: MouseEvent) {
		// Prevent the documentClick handler from immediately closing it
		if (event)
			event.stopPropagation();

		if (this.showingNotifications) {
			this.hideNotificationWindow();

			return;
		}

		this.showingNotifications = true;

		document.addEventListener("click", this.fnDocumentClick, true);
		this.documentClickAttached = true;
	}

	/** Acting on a card takes it off the alert stack - it has been dealt with. */
	private clearAlert(item: INotificationItemModel) {
		if (this.alertKeys.delete(item.key))
			this.refresh();
	}

	/**
	 * Sends the card into the flag, so it's clear the notification hasn't been thrown away - it's waiting
	 * behind the flag until the snooze is up.
	 *
	 * What flies is a COPY, appended to <body>.  The card itself can't: the stack sets `overflow-y: auto`
	 * to cap its height, which clips anything leaving its box, and the flag is above the top of it - so
	 * animating the real card just cut it off. The copy also outlives the card, which leaves the stack
	 * immediately so the ones under it close the gap while the copy is still travelling.
	 */
	/**
	 * The cards currently on screen, by key.  Read off the data attribute rather than by position, so
	 * the stack's own header rows don't shift the mapping.
	 *
	 * Whichever surface is showing: the panel hides the stack while it's open, and both draw the same
	 * card, so the one being snoozed is in exactly one of them.  Looking only in the stack, as this used
	 * to, meant a snooze from the open panel found nothing to fly and did not animate at all - which is
	 * every snooze of a reminder that was already sitting in the list.
	 */
	private cardElementsByKey(): Map<string, HTMLElement> {
		const elementsByKey = new Map<string, HTMLElement>();

		const container = this.showingNotifications
			? this.notificationPanelRef?.nativeElement
			: this.notificationAlertsRef?.nativeElement;

		container?.querySelectorAll("[data-notification-key]")
			.forEach((element: Element) => {
				elementsByKey.set(element.getAttribute("data-notification-key"), <HTMLElement>element);
			});

		return elementsByKey;
	}

	private async flyToFlag(items: INotificationItemModel[]) {
		const flagElement = this.flagRef?.nativeElement;
		const elementsByKey = this.cardElementsByKey();

		// Everything is measured and copied before anything leaves the stack, so Snooze All lifts the
		// whole stack off together instead of one card at a time.
		const flyers: { element: HTMLElement, deltaX: number, deltaY: number }[] = [];
		const flag = flagElement?.getBoundingClientRect();

		for (const item of items) {
			const cardElement = elementsByKey.get(item.key);

			// Off the stack either way - it is being snoozed, so it stops being something that arrived.
			this.alertKeys.delete(item.key);

			if (!cardElement || !flag)
				continue;

			const card = cardElement.getBoundingClientRect();
			const element = <HTMLElement>cardElement.cloneNode(true);

			element.classList.add("notification-flyer");
			element.style.top = `${card.top}px`;
			element.style.left = `${card.left}px`;
			element.style.width = `${card.width}px`;
			element.style.height = `${card.height}px`;

			document.body.appendChild(element);

			// Centre to centre, shrinking on the way in.
			flyers.push({
				element,
				deltaX: (flag.left + flag.width / 2) - (card.left + card.width / 2),
				deltaY: (flag.top + flag.height / 2) - (card.top + card.height / 2)
			});
		}

		// The cards leave the stack now, so anything under them closes the gap while the copies fly.
		this.refresh();

		if (flyers.length === 0)
			return;

		// Force the start state to be computed, or the browser coalesces it with the transforms below
		// and the copies are simply at the flag with nothing in between.
		void flyers[0].element.offsetHeight;

		for (const flyer of flyers) {
			flyer.element.style.transform = `translate(${flyer.deltaX}px, ${flyer.deltaY}px) scale(.08)`;
			flyer.element.style.opacity = "0";
		}

		await SleepService.sleep(NotificationsComponent.FLY_TO_FLAG_MS);

		flyers.forEach(x => x.element.remove());

		// Only once they've landed - the flag answering back is what says "it's in here now".
		this.flagPulse = true;

		await SleepService.sleep(NotificationsComponent.FLAG_PULSE_MS);

		this.flagPulse = false;
	}

	toggleSnoozeOptions(item: INotificationItemModel) {
		const showSnoozeOptions = !item.showSnoozeOptions;

		// Only one snooze row open at a time - six buttons on every card would swamp the panel.
		this.notifications.forEach(x => x.showSnoozeOptions = false);

		item.showSnoozeOptions = showSnoozeOptions;
	}

	//*****************************************************************************
	//* Actions
	//*****************************************************************************

	/**
	 * Dismiss clears the notification for good.  The two reminders have no row to clear, so the task
	 * one is cleared the way the old reminder dialog cleared it - a zero-minute snooze, which drops the
	 * task's next reminder - and the appointment one is written down by the store that raised it.
	 * Taking the card off the list is not enough on its own for either: the next page load re-raises
	 * anything that is still due.
	 */
	async dismiss(item: INotificationItemModel) {
		switch (item.source) {
			case "notification": {
				const result = await this.notificationsService.clearNotification(item.notificationId);

				if (result !== "Ok")
					return;

				break;
			}
		}

		this.removeItem(item);
	}

	async clearAllNotifications() {
		if (this.notifications.some(x => x.source === "notification")) {
			const result = await this.notificationsService.clearAllNotifications();

			if (result !== "Ok")
				return;
		}

		this.notifications = [];
		this.alertKeys.clear();

		this.refresh();
		this.hideNotificationWindow();
	}

	/**
	 * Snooze puts the card away without clearing it.  A server notification carries its own SnoozeUntil,
	 * so that one survives a reload; a task reminder is re-timed on the task itself, and an appointment
	 * reminder is re-timed in the browser by the store that raised it.
	 */
	async snooze(item: INotificationItemModel, snoozeMinutes: number) {
		item.showSnoozeOptions = false;
		item.snoozeUntil = moment().add(snoozeMinutes, "minutes").toDate();

		// Started before the saving below rather than after it.  flyToFlag measures and copies the card
		// up front, synchronously, and saving a task snooze re-arms the task reminders - which re-renders
		// the stack and would take the card out from under the animation.
		const flight = this.flyToFlag([item]);

		await this.saveSnooze(item, snoozeMinutes);
		await flight;

		this.refresh();

		if (this.activeNotifications.length === 0)
			this.hideNotificationWindow();
	}

	/** The saving half of a snooze.  snooze() owns the animation and the list; this owns the record. */
	private async saveSnooze(item: INotificationItemModel, snoozeMinutes: number) {
		switch (item.source) {
			case "notification": {
				const notificationModel = await this.notificationsService.snoozeNotification(item.notificationId, snoozeMinutes);

				if (notificationModel)
					item.snoozeUntil = notificationModel.snoozeUntil;

				break;
			}
		}
	}

	/** Bulk snooze row on the stack, open or closed. */
	toggleAlertSnoozeOptions() {
		this.showAlertSnoozeOptions = !this.showAlertSnoozeOptions;
	}

	/**
	 * Snooze everything currently stacked up.  One flight for the lot rather than one per card, which
	 * would take the better part of a second each.
	 */
	async snoozeAllAlerts(snoozeMinutes: number) {
		this.showAlertSnoozeOptions = false;

		// A copy: the stack is rebuilt as they leave it.
		const items = [...this.alertNotifications];

		if (items.length === 0)
			return;

		items.forEach(x => {
			x.showSnoozeOptions = false;
			x.snoozeUntil = moment().add(snoozeMinutes, "minutes").toDate();
		});

		const flight = this.flyToFlag(items);

		for (const item of items)
			await this.saveSnooze(item, snoozeMinutes);

		await flight;

		this.refresh();

		if (this.activeNotifications.length === 0)
			this.hideNotificationWindow();
	}

	/**
	 * Dismiss everything currently stacked up - only what's on the stack, not the whole list.  The panel
	 * has its own Dismiss All for that, and clearing things the user hasn't seen isn't what this asks for.
	 */
	async dismissAllAlerts() {
		this.showAlertSnoozeOptions = false;

		for (const item of [...this.alertNotifications])
			await this.dismiss(item);
	}

	/**
	 * Only ever shown on a task reminder, the same as on the reminder dialog it replaces.  The card only
	 * carries the task id, so the task is fetched first.  Completing it deletes its reminder rows
	 * server-side (TaskServices.UpdateTask), so there is nothing left here to clear.
	 */
	async completeTask(event: Event, item: INotificationItemModel) {
		event.preventDefault();
		event.stopPropagation();

		item.isCompleting = true;

		const taskModel = await this.tasksService.getTask(item.taskId);

		if (!taskModel) {
			this.removeItem(item);
			return;
		}

		taskModel.isCompleted = true;

		const updatedTaskModel = await this.tasksService.updateTask(taskModel);

		this.tasksStore.removeTask(updatedTaskModel.taskId);

		this.removeItem(item);

		this.slickToastService.showSuccess("Task completed");
	}

	async viewJob(item: INotificationItemModel) {
		this.hideNotificationWindow();
		this.clearAlert(item);

		this.jobModel = await this.jobsService.getJob(item.jobId);

		await this.jobEditDialogRef.showDialog();
	}

	onJobSave() {
		this.slickToastService.showSuccess("Job Updated");
	}

	/**
	 * The card only carries the id, so the task has to be fetched first.  One that has since been deleted
	 * just closes the panel rather than opening an empty dialog.
	 */
	async viewTask(item: INotificationItemModel) {
		this.hideNotificationWindow();
		this.clearAlert(item);

		const taskModel = await this.tasksService.getTask(item.taskId);

		if (!taskModel)
			return;

		await this.taskEditDialogRef.showDialog({ ...taskModel });
	}

	async viewAppointment(item: INotificationItemModel) {
		this.hideNotificationWindow();
		this.clearAlert(item);

		const appointmentModel = await this.appointmentsService.getAppointment(item.appointmentId);

		if (!appointmentModel)
			return;

		// Only service tech and subcontractor appointments have an editor.  An event or an employee
		// block falls back to the job it sits on, which is the useful thing to open anyway.
		if (appointmentModel.appointmentType !== AppointmentTypes.ServiceTech
			&& appointmentModel.appointmentType !== AppointmentTypes.Subcontractor) {
			if (item.jobId)
				await this.viewJob(item);

			return;
		}

		// Set straight onto the dialog rather than through an @Input - showDialog() reads it in the same
		// tick, before a binding would have been flushed.
		this.appointmentDialogRef.jobId = appointmentModel.jobId;

		await this.appointmentDialogRef.showDialog(appointmentModel);
	}

	async viewQuickbooksTask(item: INotificationItemModel) {
		this.hideNotificationWindow();
		this.clearAlert(item);

		const qbwcTaskModel = await this.quickbooksService.getQBWCTask(item.quickbooksTaskId);

		this.quickbooksTaskDisplayDialog.showDialog(qbwcTaskModel);
	}

	goToPage(item: INotificationItemModel) {
		this.hideNotificationWindow();
		this.clearAlert(item);

		this.router.navigate([item.route]);
	}

	//*****************************************************************************
	//* Closing
	//*****************************************************************************

	private detachDocumentClick() {
		if (this.documentClickAttached === false)
			return;

		document.removeEventListener("click", this.fnDocumentClick, true);
		this.documentClickAttached = false;
	}

	hideNotificationWindow() {
		this.detachDocumentClick();

		this.notifications.forEach(x => x.showSnoozeOptions = false);
		this.showingNotifications = false;
	}

	documentClick(e) {
		// If click is inside the notifications panel or the flag, do nothing
		if (UtilsService.checkParentClassExists(e.target, "notification-details") ||
			UtilsService.checkParentClassExists(e.target, "notifications")) {
			return;
		}

		this.hideNotificationWindow();
	}
}
